import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDay, faLocationDot } from "@fortawesome/free-solid-svg-icons";
import { getCityDualDisplayName, getTripDisplay } from "../../utils/tripDisplay";
import {
  FREE_DAY_SELECTION_ID,
  getTripOptionsForDay,
  getGlobalDayNumber,
} from "../../utils/tourTripOptions";

const formatDate = (dateString, locale) =>
  new Date(dateString).toLocaleDateString(locale, {
    timeZone: "Europe/Sarajevo",
    day: "2-digit",
    month: "short",
  });

const FreeDayOptionCard = ({
  t,
  isSelected,
  isDisabled,
  badge,
  onSelect,
  onSwap,
  showSwap,
}) => (
  <Card
    cursor={isDisabled ? "not-allowed" : "pointer"}
    variant="outline"
    w="full"
    borderRadius="xl"
    borderWidth={2}
    borderColor={isSelected ? "teal.500" : "gray.100"}
    bg={isSelected ? "teal.50" : "white"}
    opacity={isDisabled ? 0.55 : 1}
    transition="all 0.2s"
    _hover={isDisabled ? {} : { borderColor: "teal.300", shadow: "md" }}
    onClick={() => {
      if (isDisabled) return;
      onSelect?.();
    }}
  >
    <CardBody p={{ base: 4, md: 5 }}>
      <HStack justify="space-between" mb={3} align="flex-start" gap={3}>
        <Box
          w={5}
          h={5}
          mt={0.5}
          borderRadius="full"
          borderWidth={2}
          borderColor={isSelected ? "teal.500" : "gray.300"}
          bg={isSelected ? "teal.500" : "white"}
          flexShrink={0}
        />
        <Flex gap={2} flexWrap="wrap" justify="flex-end" flex={1}>
          {badge}
          {showSwap && onSwap && (
            <Button
              size="xs"
              variant="outline"
              colorScheme="teal"
              onClick={(event) => {
                event.stopPropagation();
                onSwap();
              }}
            >
              {t("tours.builder.swapWithThisDay")}
            </Button>
          )}
        </Flex>
      </HStack>
      <HStack spacing={3} align="flex-start">
        <Flex
          w={10}
          h={10}
          borderRadius="lg"
          bg="gray.100"
          align="center"
          justify="center"
          flexShrink={0}
        >
          <FontAwesomeIcon icon={faCalendarDay} color="#A0AEC0" />
        </Flex>
        <Box>
          <Text color="gray.700" fontWeight="medium" fontSize={{ base: "sm", md: "md" }}>
            {t("tours.tripCard.freeActivityDay")}
          </Text>
          <Text fontSize="sm" color="gray.500" mt={1}>
            {t("tours.builder.freeDayHint")}
          </Text>
        </Box>
      </HStack>
    </CardBody>
  </Card>
);

const TripOptionCard = ({
  trip,
  t,
  language,
  isSelected,
  isDisabled,
  badge,
  onSelect,
  onSwap,
  showSwap,
}) => {
  const display = getTripDisplay(trip, t, language);

  return (
    <Card
      cursor={isDisabled ? "not-allowed" : "pointer"}
      variant="outline"
      w="full"
      borderRadius="xl"
      borderWidth={2}
      borderColor={isSelected ? "teal.500" : "gray.100"}
      bg={isSelected ? "teal.50" : "white"}
      opacity={isDisabled ? 0.55 : 1}
      transition="all 0.2s"
      _hover={isDisabled ? {} : { borderColor: "teal.300", shadow: "md" }}
      onClick={() => {
        if (isDisabled) return;
        onSelect?.();
      }}
    >
      <CardBody p={{ base: 4, md: 5 }}>
        <HStack justify="space-between" mb={3} align="flex-start" gap={3}>
          <Box
            w={5}
            h={5}
            mt={0.5}
            borderRadius="full"
            borderWidth={2}
            borderColor={isSelected ? "teal.500" : "gray.300"}
            bg={isSelected ? "teal.500" : "white"}
            flexShrink={0}
          />
          <Flex gap={2} flexWrap="wrap" justify="flex-end" flex={1}>
            {badge}
            {showSwap && onSwap && (
              <Button
                size="xs"
                variant="outline"
                colorScheme="teal"
                onClick={(event) => {
                  event.stopPropagation();
                  onSwap();
                }}
              >
                {t("tours.builder.swapWithThisDay")}
              </Button>
            )}
          </Flex>
        </HStack>
        <Text color="gray.700" mb={3} lineHeight="tall" fontSize={{ base: "sm", md: "md" }}>
          {display.description}
        </Text>
        <Flex gap={2} flexWrap="wrap">
          {display.attractions.map((attr, index) => (
            <Tag
              key={index}
              size="sm"
              variant="subtle"
              colorScheme="teal"
              borderRadius="full"
            >
              <FontAwesomeIcon icon={faLocationDot} size="xs" />
              &nbsp;{attr}
            </Tag>
          ))}
        </Flex>
      </CardBody>
    </Card>
  );
};

const TripChangeModal = ({
  isOpen,
  onClose,
  currentTripData,
  tour,
  template,
  language,
  dateLocale,
  selectedTripId,
  onSelectTripId,
  onApply,
  onSwap,
}) => {
  const { t } = useTranslation("home");

  const options = useMemo(() => {
    if (
      !tour ||
      currentTripData.selectedStayIndex == null ||
      currentTripData.selectedTripIndex == null
    ) {
      return null;
    }

    return getTripOptionsForDay(tour, template, {
      stayIndex: currentTripData.selectedStayIndex,
      tripIndex: currentTripData.selectedTripIndex,
    });
  }, [tour, template, currentTripData.selectedStayIndex, currentTripData.selectedTripIndex]);

  const slot = options?.current;
  const isFreeDay = options?.isFreeDay;
  const city = tour?.[currentTripData.selectedStayIndex]?.destination;

  const isSelectedTripUsedElsewhere =
    selectedTripId &&
    selectedTripId !== FREE_DAY_SELECTION_ID &&
    options?.used?.some(({ trip }) => trip.id === selectedTripId);

  const isSelectionUnchanged =
    (isFreeDay && selectedTripId === FREE_DAY_SELECTION_ID) ||
    (!isFreeDay && selectedTripId === options?.current?.id);

  const hasOptions =
    options &&
    (options.showFreeDayOption ||
      options.available.length > 0 ||
      options.used.length > 0 ||
      options.usedFreeDays.length > 0);

  const usedOnLabel = (usedOn) =>
    t("tours.builder.usedOnDay", {
      day: usedOn.dayNumber,
      date: formatDate(usedOn.date, dateLocale),
    });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "full", sm: "lg" }}
      scrollBehavior="inside"
      isCentered
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        borderRadius={{ base: 0, sm: "xl" }}
        mx={{ base: 0, sm: 4 }}
        my={{ base: 0, sm: 4 }}
        maxH={{ base: "100dvh", sm: "90vh" }}
      >
        <ModalHeader pb={2}>
          <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="700">
            {t("tours.builder.chooseTrip")}
          </Text>
              {slot && (
            <Text fontSize="sm" color="gray.500" fontWeight="normal" mt={1}>
              {t("tours.builder.chooseTripContext", {
                city: getCityDualDisplayName(city, t, language),
                day: getGlobalDayNumber(
                  tour,
                  currentTripData.selectedStayIndex,
                  currentTripData.selectedTripIndex
                ),
                date: formatDate(slot.date, dateLocale),
              })}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px={{ base: 4, md: 6 }} py={4}>
          {!options ? null : (
            <VStack gap={4} align="stretch">
              {options.current && (
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    color="teal.600"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    mb={2}
                  >
                    {t("tours.builder.currentSelection")}
                  </Text>
                  {isFreeDay ? (
                    <FreeDayOptionCard
                      t={t}
                      isSelected={selectedTripId === FREE_DAY_SELECTION_ID}
                      onSelect={() => onSelectTripId(FREE_DAY_SELECTION_ID)}
                    />
                  ) : (
                    <TripOptionCard
                      trip={options.current}
                      t={t}
                      language={language}
                      isSelected={selectedTripId === options.current.id}
                      onSelect={() => onSelectTripId(options.current.id)}
                    />
                  )}
                </Box>
              )}

              {(options.showFreeDayOption ||
                options.available.length > 0) && (
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    mb={2}
                  >
                    {t("tours.builder.availableTrips")}
                  </Text>
                  <VStack gap={3} align="stretch">
                    {options.showFreeDayOption && (
                      <FreeDayOptionCard
                        t={t}
                        isSelected={selectedTripId === FREE_DAY_SELECTION_ID}
                        onSelect={() => onSelectTripId(FREE_DAY_SELECTION_ID)}
                      />
                    )}
                    {options.available.map(({ trip }) => (
                      <TripOptionCard
                        key={trip.id}
                        trip={trip}
                        t={t}
                        language={language}
                        isSelected={selectedTripId === trip.id}
                        onSelect={() => onSelectTripId(trip.id)}
                      />
                    ))}
                  </VStack>
                </Box>
              )}

              {(options.used.length > 0 || options.usedFreeDays.length > 0) && (
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    mb={2}
                  >
                    {t("tours.builder.usedInCitySection")}
                  </Text>
                  <VStack gap={3} align="stretch">
                    {options.usedFreeDays.map(({ usedOn }) => (
                      <FreeDayOptionCard
                        key={`free-${usedOn.stayIndex}-${usedOn.tripIndex}`}
                        t={t}
                        isSelected={selectedTripId === FREE_DAY_SELECTION_ID}
                        badge={
                          <Badge colorScheme="gray" fontSize="xs" whiteSpace="normal">
                            {usedOnLabel(usedOn)}
                          </Badge>
                        }
                        showSwap
                        onSwap={() => onSwap(usedOn)}
                        onSelect={() => onSelectTripId(FREE_DAY_SELECTION_ID)}
                      />
                    ))}
                    {options.used.map(({ trip, usedOn }) => (
                      <TripOptionCard
                        key={`used-${trip.id}`}
                        trip={trip}
                        t={t}
                        language={language}
                        isSelected={selectedTripId === trip.id}
                        badge={
                          <Badge colorScheme="gray" fontSize="xs" whiteSpace="normal">
                            {usedOnLabel(usedOn)}
                          </Badge>
                        }
                        showSwap
                        onSwap={() => onSwap(usedOn)}
                        onSelect={() => onSelectTripId(trip.id)}
                      />
                    ))}
                  </VStack>
                </Box>
              )}

              {!hasOptions && (
                <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                  {t("tours.builder.noAlternativeTrips")}
                </Text>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter gap={3} px={{ base: 4, md: 6 }} pb={{ base: 4, md: 6 }}>
          <Button variant="ghost" onClick={onClose}>
            {t("tours.builder.cancel")}
          </Button>
          <Button
            colorScheme="teal"
            onClick={onApply}
            isDisabled={
              !selectedTripId || isSelectionUnchanged || isSelectedTripUsedElsewhere
            }
          >
            {t("tours.builder.save")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TripChangeModal;
